import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { IoArrowBackOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";
import {
  addVideo,
  addVideoQuestions,
  clearSelectedVideo,
  fetchVideoById,
  fetchVideoTags,
  updateVideo,
} from "../../../store/reducers/videoSectionSlice";
import DraggableSelectList from "../questionsSection/components/QuestionListDropdown";
import { fetchActiveQuestions } from "../../../store/reducers/questionsSectionSlice";

const buildDropdownOptions = (items = []) =>
  [...new Set(items.map((item) => `${item}`.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((item) => ({
      label: item,
      value: item,
    }));

const ensureOption = (options, value) => {
  const normalizedValue = `${value || ""}`.trim();

  if (!normalizedValue) return options;
  if (options.some((option) => option.value === normalizedValue)) return options;

  return [
    ...options,
    {
      label: normalizedValue,
      value: normalizedValue,
    },
  ];
};

const VideoAddUpdate = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const videoId = params.get("id");

  const { selectedVideo, singleLoading } = useSelector(
    (state) => state.eduVideos
  );
  const { videoTags } = useSelector((state) => state.eduVideos);

  const [options, setOptions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [defaultQuestionIds, setDefaultQuestionIds] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const { activeQuestions } = useSelector((state) => state.questions);

  useEffect(() => {
    dispatch(fetchActiveQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (activeQuestions) {
      const formatted = activeQuestions.map((q) => ({
        label: q.question,
        value: q.id,
      }));

      setOptions(formatted);
    }
  }, [activeQuestions]);
  
  const handleSelectedIds = (ids) => {
    setSelectedQuestionIds(ids);
  };

  const [formData, setFormData] = useState({
    date: "",
    version: "1",
    tag: "",
    active: true,
    name_en: "",
    link_en: "",
    name_dk: "",
    link_dk: "",
  });
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    dispatch(fetchVideoTags());
  }, [dispatch]);

  useEffect(() => {
    setTagOptions((prev) =>
      ensureOption(
        buildDropdownOptions([...(videoTags || []), ...prev.map((item) => item.value)]),
        formData.tag
      )
    );
  }, [videoTags, formData.tag]);

  const vimeoRegex = /^(https?:\/\/)?(www\.)?(player\.)?vimeo\.com\/(video\/)?(\d+)([^\s]*)?$/;

  // FETCH VIDEO
  useEffect(() => {
    if (videoId) {
      dispatch(fetchVideoById(videoId));
    } else {
      setFormData({
        date: new Date().toISOString().split("T")[0],
        version: "v1",
        tag: "",
        active: true,
        name_en: "",
        link_en: "",
        name_dk: "",
        link_dk: "",
      });
    }

    return () => dispatch(clearSelectedVideo());
  }, [videoId]);

  // POPULATE
  useEffect(() => {
    if (selectedVideo && videoId) {
      setFormData({
        date: selectedVideo.date_added || "",
        version: selectedVideo.version || "v1",
        tag: selectedVideo.tag || "",
        active: selectedVideo.active ?? true,
        name_en: selectedVideo.name || "",
        link_en: selectedVideo.link || "",
        name_dk: selectedVideo.name_dk || "",
        link_dk: selectedVideo.link_dk || "",
      });

      if (selectedVideo.question_ids) {
        setSelectedQuestionIds(selectedVideo.question_ids || []);
        setDefaultQuestionIds(selectedVideo.question_ids || []);
      }

      setTagOptions((prev) => ensureOption(prev, selectedVideo.tag));
    }
  }, [selectedVideo]);

  // CHANGE
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleTagChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      tag: selectedOption?.value || "",
    }));

    setErrors((prev) => ({
      ...prev,
      tag: "",
    }));
  };

  const handleCreateTag = (inputValue) => {
    const normalizedValue = inputValue.trim();

    if (!normalizedValue) return;

    setTagOptions((prev) => ensureOption(prev, normalizedValue));
    setFormData((prev) => ({
      ...prev,
      tag: normalizedValue,
    }));
    setErrors((prev) => ({
      ...prev,
      tag: "",
    }));
  };

  // VALIDATION
  const validateForm = () => {
    let newErrors = {};

    // REQUIRED NAME
    if (!formData.name_en.trim()) {
      newErrors.name_en = "English name is required";
    }

    if (!formData.name_dk.trim()) {
      newErrors.name_dk = "Danish name is required";
    }

    // REQUIRED + VIMEO VALIDATION (ENGLISH)
    if (!formData.link_en.trim()) {
      newErrors.link_en = "English video URL is required";
    } else if (!vimeoRegex.test(formData.link_en.trim())) {
      newErrors.link_en = "Enter a valid Vimeo URL";
    }

    // REQUIRED + VIMEO VALIDATION (DANISH)
    if (!formData.link_dk.trim()) {
      newErrors.link_dk = "Danish video URL is required";
    } else if (!vimeoRegex.test(formData.link_dk.trim())) {
      newErrors.link_dk = "Enter a valid Vimeo URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      let currentVideoId = videoId;

      // ADD VIDEO
      if (!videoId) {
        const res = await dispatch(addVideo(formData)).unwrap();

        // handle different API response shapes
        currentVideoId = res?.id || res?.data?.id || res?.video_id;

        if (!currentVideoId) {
          toast.error("Video created but ID not returned");
          return;
        }

        toast.success("Video Added Successfully");
      }
      // UPDATE VIDEO
      else {
        await dispatch(updateVideo({ id: videoId, data: formData })).unwrap();
        currentVideoId = videoId;
        toast.success("Video Updated Successfully");
      }

      // ADD QUESTIONS
      if (selectedQuestionIds.length > 0) {
        await dispatch(
          addVideoQuestions({
            video_id: Number(currentVideoId),
            question_ids: selectedQuestionIds,
          })
        ).unwrap();
      }

      history.push("/admin-edu-videos");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  return (
    <div className=" mt-3">
      {/* HEADER */}
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-outline-secondary btn-sm me-3"
          onClick={() => history.push("/admin-edu-videos")}
        >
          <IoArrowBackOutline /> Back
        </button>

        <h5 className="mb-0 fw-bold">
          {videoId ? "Update Educational Video" : "Add Educational Video"}
        </h5>
      </div>

      {singleLoading ? (
        <div className="text-center p-5">Loading video...</div>
      ) : (
        <form onSubmit={handleSubmit} className="p-2">
          {/* TOP ROW */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.date}
                disabled
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Version</label>
              <input
                type="text"
                className="form-control"
                name="version"
                value={formData.version}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Tag</label>
              <CreatableSelect
                isClearable
                name="tag"
                options={tagOptions}
                value={
                  formData.tag
                    ? { label: formData.tag, value: formData.tag }
                    : null
                }
                onChange={handleTagChange}
                onCreateOption={handleCreateTag}
                placeholder="Select or type tag"
              />
            </div>

            <div className="col-md-3 d-flex align-items-center">
              <label className="form-label mb-0 me-5">Active</label>

              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <hr />

          {/* ENGLISH SECTION */}
          <h6 className="fw-bold mb-3">English Video</h6>

          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label">Name (English)*</label>
              <input
                type="text"
                name="name_en"
                className={`form-control ${errors.name_en ? "is-invalid" : ""}`}
                value={formData.name_en}
                onChange={handleChange}
              />
              <div className="invalid-feedback">{errors.name_en}</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Video Link (English)*</label>
              <input
                type="text"
                name="link_en"
                placeholder="https://vimeo.com/123456789"
                className={`form-control ${errors.link_en ? "is-invalid" : ""}`}
                value={formData.link_en}
                onChange={handleChange}
              />
              <div className="invalid-feedback">{errors.link_en}</div>
            </div>
          </div>

          <hr />

          {/* DANISH SECTION */}
          <h6 className="fw-bold mb-3">Danish Video</h6>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Name (Danish)*</label>
              <input
                type="text"
                name="name_dk"
                className={`form-control ${errors.name_dk ? "is-invalid" : ""}`}
                value={formData.name_dk}
                onChange={handleChange}
              />
              <div className="invalid-feedback">{errors.name_dk}</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Video Link (Danish)*</label>
              <input
                type="text"
                name="link_dk"
                 placeholder="https://vimeo.com/123456789"
                className={`form-control ${errors.link_dk ? "is-invalid" : ""}`}
                value={formData.link_dk}
                onChange={handleChange}
              />
              <div className="invalid-feedback">{errors.link_dk}</div>
            </div>
          </div>

          <hr />
          <label>Select Questions: </label>
          <DraggableSelectList
            options={options}
            defaultValues={defaultQuestionIds}
            onChange={handleSelectedIds}
          />
          <hr />
          {/* BUTTON */}
          <div className="mt-4">
            <button type="submit" className="btn bg_color px-4">
              {videoId ? "Update Educational Video" : "Add Educational Video"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default VideoAddUpdate;
